import {
  ActivityLogAssociatedModelDescription,
  ActivityLogInitiatorDescription,
} from '../../models/ActivityLog';
import ContentManagementInterface from '../../models/ContentManagementInterface';
import { ContentDiffPayload } from './contentDiffTypes';

export function getActivityLogDescriptionDisplayName(
  description?: ActivityLogInitiatorDescription | ActivityLogAssociatedModelDescription | null,
): string {
  if (!description) return 'Unknown';
  if (description.name) return description.name;
  if (description.contactName && description.fundOpportunityName) {
    return `${description.contactName} (${description.fundOpportunityName})`;
  }
  if (description.contactName) return description.contactName;
  if (description.fundOpportunityName) return description.fundOpportunityName;
  if (description.entityName) return description.entityName;
  return 'Unknown';
}

export function getModelNavigationPath(type: string, id: string): string {
  const modelTypeToPath: Record<string, string> = {
    CONTACT: `/contacts/${id}`,
    ADMIN_USER: `/adminUsers/${id}`,
    INVESTOR: `/investors/${id}`,
    FUND_OPPORTUNITY: `/opportunities/${id}`,
    INVESTMENT_ENTITY: `/entities/${id}`,
    EOI: `/opportunities/${id}`,
    ALLOCATION: `/allocations/${id}`,
    GLOBAL_CONFIG: `/globalConfig`,
    USER: `/users/${id}`,
    ARTICLE: `/articles/${id}`,
    // Opens the Activity Logs "Client exceptions" tab on that group.
    CLIENT_EXCEPTION: `/activityLogs?tab=client-exceptions&group=${encodeURIComponent(id)}`,
  };
  return modelTypeToPath[type] || `/models/${type.toLowerCase()}/${id}`;
}

/**
 * Apply a diff in reverse to reconstruct the previous version of content.
 */
export function applyDiffReverse(
  currentContent: ContentManagementInterface,
  diff: ContentDiffPayload,
): ContentManagementInterface | null {
  try {
    const previousContent = JSON.parse(
      JSON.stringify(currentContent),
    ) as ContentManagementInterface;

    for (const entry of diff.entries || []) {
      const path = entry.path;
      const changeType = entry.changeType;
      const pathParts = path.split('.');
      let target: any = previousContent;

      for (let i = 0; i < pathParts.length - 1; i++) {
        const part = pathParts[i];
        const isArrayIndex = /^\[(\d+)\]$/.test(part);

        if (isArrayIndex) {
          const index = parseInt(part.slice(1, -1), 10);
          target = target[index];
        } else {
          if (!target[part]) {
            target[part] = {};
          }
          target = target[part];
        }
      }

      const finalKey = pathParts[pathParts.length - 1];
      const isArrayIndex = /^\[(\d+)\]$/.test(finalKey);

      if (isArrayIndex) {
        const index = parseInt(finalKey.slice(1, -1), 10);

        switch (changeType) {
          case 'added':
            if (Array.isArray(target) && index < target.length) {
              target.splice(index, 1);
            }
            break;
          case 'removed':
            if (Array.isArray(target) && entry.before !== undefined) {
              target.splice(index, 0, entry.before);
            }
            break;
          case 'changed':
            if (Array.isArray(target) && index < target.length) {
              target[index] = entry.before;
            }
            break;
        }
      } else {
        switch (changeType) {
          case 'added':
            delete target[finalKey];
            break;
          case 'removed':
            if (entry.before !== undefined) {
              target[finalKey] = entry.before;
            }
            break;
          case 'changed':
            target[finalKey] = entry.before;
            break;
        }
      }
    }

    return previousContent;
  } catch (error) {
    console.error('Error applying diff in reverse:', error);
    return null;
  }
}
